import {
  getTeamsByTeamExternalIdLeaderboardOptions,
  ParameterorderBy,
  TeamsTopHitterStats,
  TeamsTopPitcherStats,
} from "@aura-frontend/omnibus-az-client";
import { type as GameType } from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { getCalendarYearRange } from "@/utils/dateUtils";

import { FilterBy, SortBy } from "../types";

const LEADER_BOARD_STATS: { [key: string]: ParameterorderBy } = {
  exitVelocity: "max_exit_velocity",
  distance: "max_distance",
  velocity: "max_fb_velocity",
  inVertBreak: "max_fb_ivb",
};

export const useTeamTopPlayers = (
  teamId: string,
  filter: FilterBy,
  sortBy: SortBy,
  gameType: GameType,
  year: number = new Date().getFullYear(),
) => {
  const { startDate, endDate } = getCalendarYearRange(year);
  const {
    data: topPlayersData,
    isLoading: isLoadingTopPlayers,
    error: topPlayersError,
  } = useQuery({
    ...getTeamsByTeamExternalIdLeaderboardOptions({
      path: { team_external_id: teamId },
      query: {
        playerType: filter === "hitting" ? "hitter" : "pitcher",
        orderBy: LEADER_BOARD_STATS[sortBy],
        gameType,
        startDate,
        endDate,
      },
    }),
    enabled: !!teamId,
  });

  const leaderboardHittersData = useMemo(() => {
    if (filter !== "hitting") return [];
    if (topPlayersData && topPlayersData.length > 0) {
      return topPlayersData.map((player: TeamsTopHitterStats) => ({
        name: player.player_name,
        id: player.player_external_id,
        exitVelocity: player.max_exit_velocity,
        distance: player.max_distance,
        launchAngle: player.max_launch_angle,
      }));
    }
    return [];
  }, [filter, topPlayersData]);

  const leaderboardPitchersData = useMemo(() => {
    if (filter === "hitting") return [];
    if (topPlayersData && topPlayersData.length > 0) {
      return topPlayersData.map((player: TeamsTopPitcherStats) => ({
        name: player.player_name,
        id: player.player_external_id,
        velocity: player.max_fb_velocity,
        inVertBreak: player.max_fb_ivb,
        horizontalBreak: player.max_horz_break,
      }));
    }
    return [];
  }, [filter, topPlayersData]);

  return {
    hitters: leaderboardHittersData,
    pitchers: leaderboardPitchersData,
    isLoadingTopPlayers,
    topPlayersError,
  };
};
